"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { ModeToggle } from "../components/ThemeToggle";
import { useUser } from "@clerk/nextjs";
import Logo from "@/components/layout/Logo";
import LanguageDropdown from "../components/LanguageDropdown";
import { useTranslation } from "react-i18next";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  ArrowUpRight,
  BriefcaseBusiness,
  ChevronDown,
  FileCheck2,
  MailCheck,
  Menu,
  MessagesSquare,
} from "lucide-react";
import {
  getPublicLocaleFromPathname,
  localizePublicHref,
} from "@/lib/i18n/public-routes";

const featureLinks = [
  {
    id: "ai-resume-builder",
    titleKey: "NAV_TOOL_RESUME_BUILDER_TITLE",
    descKey: "NAV_TOOL_RESUME_BUILDER_DESC",
    Icon: FileCheck2,
    href: "/resume-builder",
  },
  {
    id: "job-tracker",
    titleKey: "NAV_TOOL_JOB_TRACKER_TITLE",
    descKey: "NAV_TOOL_JOB_TRACKER_DESC",
    Icon: BriefcaseBusiness,
    href: "/job-finder",
  },
  {
    id: "cover-letter-generator",
    titleKey: "NAV_TOOL_COVER_LETTER_TITLE",
    descKey: "NAV_TOOL_COVER_LETTER_DESC",
    Icon: MailCheck,
    href: "/cover-letter-generator",
  },
  {
    id: "ai-interviews",
    titleKey: "NAV_TOOL_INTERVIEWS_TITLE",
    descKey: "NAV_TOOL_INTERVIEWS_DESC",
    Icon: MessagesSquare,
    href: "/interview-prep",
  },
] as const;

const resumeExamplesLinks = [
  { id: "resume-examples-hub", labelKey: "NAV_RESUME_EXAMPLES_HUB_LABEL", href: "/resume-examples" },
  { id: "resume-examples-software", labelKey: "NAV_RESUME_EXAMPLES_SOFTWARE_LABEL", href: "/resume-examples/software-engineer" },
  { id: "resume-examples-product", labelKey: "NAV_RESUME_EXAMPLES_PRODUCT_LABEL", href: "/resume-examples/product-manager" },
  { id: "resume-examples-data", labelKey: "NAV_RESUME_EXAMPLES_DATA_LABEL", href: "/resume-examples/data-analyst" },
] as const;

const resourceLinks = [
  { id: "resource-ats", labelKey: "NAV_RESOURCE_ATS_CHECKER_LABEL", href: "/resume-checker" },
  { id: "resource-blog", labelKey: "NAV_BLOG_LABEL", href: "/blog" },
  { id: "resource-faq", labelKey: "NAV_FAQ_LABEL", href: "/#faq" },
] as const;

const navLinks = [
  { href: "/pricing", labelKey: "NAV_PRICING_LABEL" },
] as const;

const navItemClass =
  "inline-flex h-10 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[15px] font-medium leading-none tracking-[-0.01em] text-slate-600 transition-colors duration-200 hover:bg-slate-900/5 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 data-[state=open]:bg-slate-900/5 data-[state=open]:text-slate-950 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white dark:data-[state=open]:bg-white/10 dark:data-[state=open]:text-white";

const primaryCtaClass =
  "group inline-flex h-9 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-400 px-3.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 transition hover:opacity-95";

export default function NavBar() {
  const pathname = usePathname() ?? "/";
  const publicLocale = getPublicLocaleFromPathname(pathname);
  const { isSignedIn } = useUser();
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [resumeExamplesOpen, setResumeExamplesOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const featuresCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resumeExamplesCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resourcesCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { t } = useTranslation();

  const clearCloseTimeout = (ref: typeof featuresCloseTimeoutRef) => {
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }
  };

  const openFeaturesMenu = () => {
    clearCloseTimeout(featuresCloseTimeoutRef);
    setFeaturesOpen(true);
    setResumeExamplesOpen(false);
    setResourcesOpen(false);
  };

  const scheduleCloseFeaturesMenu = () => {
    clearCloseTimeout(featuresCloseTimeoutRef);
    featuresCloseTimeoutRef.current = setTimeout(() => setFeaturesOpen(false), 160);
  };

  const openResumeExamplesMenu = () => {
    clearCloseTimeout(resumeExamplesCloseTimeoutRef);
    setResumeExamplesOpen(true);
    setFeaturesOpen(false);
    setResourcesOpen(false);
  };

  const scheduleCloseResumeExamplesMenu = () => {
    clearCloseTimeout(resumeExamplesCloseTimeoutRef);
    resumeExamplesCloseTimeoutRef.current = setTimeout(
      () => setResumeExamplesOpen(false),
      160
    );
  };

  const openResourcesMenu = () => {
    clearCloseTimeout(resourcesCloseTimeoutRef);
    setResourcesOpen(true);
    setFeaturesOpen(false);
    setResumeExamplesOpen(false);
  };

  const scheduleCloseResourcesMenu = () => {
    clearCloseTimeout(resourcesCloseTimeoutRef);
    resourcesCloseTimeoutRef.current = setTimeout(() => setResourcesOpen(false), 160);
  };

  useEffect(
    () => () => {
      clearCloseTimeout(featuresCloseTimeoutRef);
      clearCloseTimeout(resumeExamplesCloseTimeoutRef);
      clearCloseTimeout(resourcesCloseTimeoutRef);
    },
    []
  );

  useEffect(() => {
    const updateScrolledState = () => {
      const y = window.scrollY;
      setIsScrolled((previous) => {
        if (previous) {
          return y > 14;
        }
        return y > 28;
      });
    };

    updateScrolledState();
    window.addEventListener("scroll", updateScrolledState, { passive: true });
    return () => window.removeEventListener("scroll", updateScrolledState);
  }, []);

  const navSurfaceClass = isScrolled
    ? "border-white/55 bg-white/90 shadow-[0_20px_55px_-42px_rgba(15,23,42,0.58)] backdrop-blur-sm dark:border-white/10 dark:bg-slate-950/90"
    : "border-transparent bg-transparent shadow-none";
  const appHref = isSignedIn ? "/dashboard" : "/sign-up";
  const appLabel = t(
    isSignedIn ? "NAV_OPEN_APP_LABEL" : "AUTH_NAV_GET_STARTED_BUTTON"
  );
  const localizedPublicHref = (href: string) =>
    localizePublicHref(publicLocale, href);

  return (
    <>
      <div aria-hidden className="h-[5.4rem] xl:h-[5.8rem]" />
      <nav
        aria-label="Main"
        className="fixed left-1/2 top-3 z-50 w-[calc(100%-1.5rem)] max-w-7xl -translate-x-1/2 [font-family:var(--font-body)] sm:top-4 sm:w-[calc(100%-3rem)] lg:w-[calc(100%-5rem)]"
      >
        <div
          className={`relative flex min-h-16 w-full items-center rounded-2xl border px-3 transition-[box-shadow,background-color,border-color] duration-200 sm:min-h-[4.5rem] sm:px-5 ${navSurfaceClass}`}
        >
          <div className="relative flex w-full items-center gap-3 xl:gap-6">
            <Link
              href={localizedPublicHref("/")}
              aria-label="LandFast Home"
              className="inline-flex shrink-0 items-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              <span className="hidden sm:inline-flex">
                <Logo variant="wordmark" hideTitle size={56} />
              </span>
              <span className="inline-flex sm:hidden">
                <Logo variant="icon" hideTitle size={44} />
              </span>
            </Link>

            <div className="hidden min-w-0 flex-1 items-center justify-center gap-1 xl:flex 2xl:gap-2">
              <Collapsible
                open={featuresOpen}
                onOpenChange={setFeaturesOpen}
                className="relative"
                onMouseEnter={openFeaturesMenu}
                onMouseLeave={scheduleCloseFeaturesMenu}
              >
                <CollapsibleTrigger
                  className={navItemClass}
                >
                  <span className="relative">{t("NAV_FEATURES_LABEL")}</span>
                  <ChevronDown
                    className={`relative h-3.5 w-3.5 text-slate-400 transition-transform ${featuresOpen ? "rotate-180" : ""}`}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent
                  className="absolute left-1/2 z-20 mt-3 w-[22rem] -translate-x-1/2 overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-2.5 shadow-[0_16px_40px_-16px_rgba(15,23,42,0.2)] backdrop-blur-xl transition data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 dark:border-white/15 dark:bg-slate-950"
                  onMouseEnter={openFeaturesMenu}
                  onMouseLeave={scheduleCloseFeaturesMenu}
                >
                  <div
                    aria-hidden
                    className="pointer-events-none absolute right-2 top-1 h-28 w-28 rounded-full bg-[radial-gradient(circle_at_center,_rgba(59,130,246,0.2),_rgba(255,255,255,0))] blur-2xl dark:bg-[radial-gradient(circle_at_center,_rgba(148,163,184,0.2),_rgba(2,6,23,0))]"
                  />
                  <div className="px-2 pb-2 pt-1 text-[0.68rem] font-medium uppercase tracking-[0.18em] text-slate-500/90 dark:text-slate-300/85">
                    {t("NAV_FEATURES_SECTION_LABEL")}
                  </div>
                  <div className="relative space-y-1">
                    {featureLinks.map((feature) => {
                      const Icon = feature.Icon;
                      return (
                        <Link
                          key={feature.id}
                          href={localizedPublicHref(feature.href)}
                          onClick={() => setFeaturesOpen(false)}
                          className="group flex items-start gap-3 rounded-xl border border-transparent px-3 py-2.5 text-left transition-all duration-200 hover:border-white/55 hover:bg-white/55 hover:pl-3.5 dark:hover:border-white/20 dark:hover:bg-white/10"
                        >
                          <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-indigo-100/80 bg-indigo-50/75 text-indigo-700 transition duration-200 group-hover:border-indigo-200 group-hover:bg-indigo-100/80 dark:border-indigo-300/15 dark:bg-indigo-400/10 dark:text-indigo-200 dark:group-hover:bg-indigo-400/15">
                            <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
                          </span>
                          <span className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-slate-800 dark:text-white">
                              {t(feature.titleKey)}
                            </span>
                            <span className="text-xs leading-5 text-slate-500/95 dark:text-slate-300/95">
                              {t(feature.descKey)}
                            </span>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <Collapsible
                open={resumeExamplesOpen}
                onOpenChange={setResumeExamplesOpen}
                className="relative"
                onMouseEnter={openResumeExamplesMenu}
                onMouseLeave={scheduleCloseResumeExamplesMenu}
              >
                <CollapsibleTrigger
                  className={navItemClass}
                >
                  <span className="relative">{t("NAV_RESUME_EXAMPLES_LABEL")}</span>
                  <ChevronDown
                    className={`relative h-3.5 w-3.5 text-slate-400 transition-transform ${resumeExamplesOpen ? "rotate-180" : ""}`}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent
                  className="absolute left-1/2 z-20 mt-3 w-[20rem] -translate-x-1/2 overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-2.5 shadow-[0_16px_40px_-16px_rgba(15,23,42,0.2)] backdrop-blur-xl transition data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 dark:border-white/15 dark:bg-slate-950"
                  onMouseEnter={openResumeExamplesMenu}
                  onMouseLeave={scheduleCloseResumeExamplesMenu}
                >
                  <div className="px-2 pb-2 pt-1 text-[0.68rem] font-medium uppercase tracking-[0.18em] text-slate-500/90 dark:text-slate-300/85">
                    {t("NAV_RESUME_EXAMPLES_SECTION_LABEL")}
                  </div>
                  <div className="grid gap-1">
                    {resumeExamplesLinks.map((link) => (
                      <Link
                        key={link.id}
                        href={localizedPublicHref(link.href)}
                        onClick={() => setResumeExamplesOpen(false)}
                        className="rounded-xl border border-transparent px-3 py-2 text-sm font-medium text-slate-700 transition duration-200 hover:border-white/55 hover:bg-white/70 hover:text-slate-900 dark:text-slate-200 dark:hover:border-white/20 dark:hover:bg-white/10 dark:hover:text-white"
                      >
                        {t(link.labelKey)}
                      </Link>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <Collapsible
                open={resourcesOpen}
                onOpenChange={setResourcesOpen}
                className="relative"
                onMouseEnter={openResourcesMenu}
                onMouseLeave={scheduleCloseResourcesMenu}
              >
                <CollapsibleTrigger
                  className={navItemClass}
                >
                  <span className="relative">{t("NAV_RESOURCES_LABEL")}</span>
                  <ChevronDown
                    className={`relative h-3.5 w-3.5 text-slate-400 transition-transform ${resourcesOpen ? "rotate-180" : ""}`}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent
                  className="absolute left-1/2 z-20 mt-3 w-[18rem] -translate-x-1/2 overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-2.5 shadow-[0_16px_40px_-16px_rgba(15,23,42,0.2)] backdrop-blur-xl transition data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 dark:border-white/15 dark:bg-slate-950"
                  onMouseEnter={openResourcesMenu}
                  onMouseLeave={scheduleCloseResourcesMenu}
                >
                  <div className="px-2 pb-2 pt-1 text-[0.68rem] font-medium uppercase tracking-[0.18em] text-slate-500/90 dark:text-slate-300/85">
                    {t("NAV_RESOURCES_SECTION_LABEL")}
                  </div>
                  <div className="grid gap-1">
                    {resourceLinks.map((link) => (
                      <Link
                        key={link.id}
                        href={localizedPublicHref(link.href)}
                        onClick={() => setResourcesOpen(false)}
                        className="rounded-xl border border-transparent px-3 py-2 text-sm font-medium text-slate-700 transition duration-200 hover:border-white/55 hover:bg-white/70 hover:text-slate-900 dark:text-slate-200 dark:hover:border-white/20 dark:hover:bg-white/10 dark:hover:text-white"
                      >
                        {t(link.labelKey)}
                      </Link>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {navLinks.map((link) => {
                return (
                  <Link
                    key={link.href}
                    href={localizedPublicHref(link.href)}
                    className={navItemClass}
                  >
                    {t(link.labelKey)}
                  </Link>
                );
              })}
            </div>

            <div className="ml-auto flex shrink-0 items-center gap-2">
              <Button asChild className={primaryCtaClass}>
                <Link
                  href={appHref}
                  aria-current={pathname.startsWith(appHref) ? "page" : undefined}
                >
                  {appLabel}
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-white transition group-hover:scale-105">
                    <ArrowUpRight aria-hidden className="h-3.5 w-3.5" strokeWidth={2.2} />
                  </span>
                </Link>
              </Button>

              <div className="ml-2 hidden items-center gap-1 border-l border-slate-900/10 pl-3 xl:flex dark:border-white/15 [&_button]:h-10 [&_button]:w-10 [&_button]:rounded-full [&_button]:border-transparent [&_button]:bg-transparent [&_button]:text-slate-500 [&_button:hover]:bg-slate-900/5 [&_button:hover]:text-slate-900 dark:[&_button]:text-slate-300 dark:[&_button:hover]:bg-white/10 dark:[&_button:hover]:text-white [&_svg]:h-[18px] [&_svg]:w-[18px]">
                <ModeToggle />
                <LanguageDropdown />
              </div>

              <div className="flex xl:hidden">
                <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                  <SheetTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 rounded-full border-slate-200/70 bg-white/60 text-slate-700 hover:bg-white dark:border-white/15 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
                    >
                      <Menu className="relative h-4 w-4" />
                      <span className="sr-only">{t("NAV_MENU_LABEL")}</span>
                    </Button>
                  </SheetTrigger>
                  <SheetContent
                    side="right"
                    className="w-[90vw] max-w-[22rem] border-l border-slate-200 bg-gradient-to-b from-slate-50 to-white p-0 [font-family:var(--font-body)] [&>button]:top-8 [&>button]:right-6 [&>button]:inline-flex [&>button]:h-10 [&>button]:w-10 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-full [&>button]:border [&>button]:border-slate-200/80 [&>button]:bg-white/75 [&>button]:text-slate-600 [&>button]:opacity-100 [&>button]:shadow-sm [&>button]:transition-[background-color,color,transform,box-shadow] [&>button]:duration-200 [&>button:hover]:rotate-90 [&>button:hover]:bg-white [&>button:hover]:text-slate-950 [&>button:hover]:shadow-md sm:[&>button]:right-[2.8125rem] dark:border-white/10 dark:from-slate-950 dark:to-slate-900 dark:[&>button]:border-white/15 dark:[&>button]:bg-white/[0.06] dark:[&>button]:text-slate-200 dark:[&>button:hover]:bg-white/15 dark:[&>button:hover]:text-white"
                  >
                    <div className="relative h-full overflow-y-auto px-4 pb-6 pt-5">
                      <div
                        aria-hidden
                        className="pointer-events-none absolute right-1 top-2 h-40 w-40 rounded-full bg-[radial-gradient(circle_at_center,_rgba(99,102,241,0.2),_rgba(255,255,255,0))] blur-3xl dark:bg-[radial-gradient(circle_at_center,_rgba(148,163,184,0.25),_rgba(2,6,23,0))]"
                      />
                      <div
                        aria-hidden
                        className="pointer-events-none absolute inset-x-4 top-16 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent dark:via-white/20"
                      />
                      <SheetHeader className="relative top-[22px] items-start text-left">
                        <SheetTitle className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-600 dark:text-slate-200">
                          {t("NAV_MENU_LABEL")}
                        </SheetTitle>
                      </SheetHeader>

                      <div className="mt-12 flex flex-col gap-3.5">
                        <Accordion type="single" collapsible defaultValue="features">
                          <AccordionItem
                            value="features"
                            className="rounded-2xl border border-white/35 bg-white/45 px-3.5  dark:border-white/10 dark:bg-white/[0.06]"
                          >
                            <AccordionTrigger className="py-3 text-sm font-semibold text-slate-700 hover:no-underline dark:text-slate-200">
                              {t("NAV_FEATURES_LABEL")}
                            </AccordionTrigger>
                            <AccordionContent className="pb-2.5">
                              <div className="grid gap-1.5">
                                {featureLinks.map((feature) => {
                                  const Icon = feature.Icon;
                                  return (
                                    <Link
                                      key={feature.id}
                                      href={localizedPublicHref(feature.href)}
                                      onClick={() => setMobileMenuOpen(false)}
                                      className="group flex items-start gap-3 rounded-xl border border-white/20 bg-white/55 py-2.5 text-left transition duration-200 hover:border-white/45 hover:bg-white/75 dark:border-white/10 dark:bg-white/[0.05] dark:hover:bg-white/10"
                                    >
                                      <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-indigo-100/80 bg-indigo-50/75 text-indigo-700 transition duration-200 group-hover:border-indigo-200 group-hover:bg-indigo-100/80 dark:border-indigo-300/15 dark:bg-indigo-400/10 dark:text-indigo-200 dark:group-hover:bg-indigo-400/15">
                                        <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
                                      </span>
                                      <span className="flex flex-col gap-1">
                                        <span className="text-sm font-semibold text-slate-800 dark:text-white">
                                          {t(feature.titleKey)}
                                        </span>
                                        <span className="text-xs leading-5 text-slate-500/95 dark:text-slate-300/95">
                                          {t(feature.descKey)}
                                        </span>
                                      </span>
                                    </Link>
                                  );
                                })}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>

                        <Accordion type="single" collapsible>
                          <AccordionItem
                            value="resume-examples"
                            className="rounded-2xl border border-white/35 bg-white/45 px-3.5 dark:border-white/10 dark:bg-white/[0.06]"
                          >
                            <AccordionTrigger className="py-3 text-sm font-semibold text-slate-700 hover:no-underline dark:text-slate-200">
                              {t("NAV_RESUME_EXAMPLES_LABEL")}
                            </AccordionTrigger>
                            <AccordionContent className="pb-2.5">
                              <div className="grid gap-1.5">
                                {resumeExamplesLinks.map((link) => (
                                  <Link
                                    key={link.id}
                                    href={localizedPublicHref(link.href)}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="rounded-xl border border-white/20 bg-white/55 px-2.5 py-2.5 text-left text-sm font-medium text-slate-700 transition duration-200 hover:border-white/45 hover:bg-white/75 dark:border-white/10 dark:bg-white/[0.05] dark:text-slate-200 dark:hover:bg-white/10"
                                  >
                                    {t(link.labelKey)}
                                  </Link>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>

                        <Accordion type="single" collapsible>
                          <AccordionItem
                            value="resources"
                            className="rounded-2xl border border-white/35 bg-white/45 px-3.5 dark:border-white/10 dark:bg-white/[0.06]"
                          >
                            <AccordionTrigger className="py-3 text-sm font-semibold text-slate-700 hover:no-underline dark:text-slate-200">
                              {t("NAV_RESOURCES_LABEL")}
                            </AccordionTrigger>
                            <AccordionContent className="pb-2.5">
                              <div className="grid gap-1.5">
                                {resourceLinks.map((link) => (
                                  <Link
                                    key={link.id}
                                    href={localizedPublicHref(link.href)}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="rounded-xl border border-white/20 bg-white/55 px-2.5 py-2.5 text-left text-sm font-medium text-slate-700 transition duration-200 hover:border-white/45 hover:bg-white/75 dark:border-white/10 dark:bg-white/[0.05] dark:text-slate-200 dark:hover:bg-white/10"
                                  >
                                    {t(link.labelKey)}
                                  </Link>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>

                        <div className="grid gap-1.5">
                          {navLinks.map((link) => (
                            <Link
                              key={link.href}
                              href={localizedPublicHref(link.href)}
                              onClick={() => setMobileMenuOpen(false)}
                              className="rounded-xl border border-white/30 bg-white/50 px-3.5 py-2.5 text-sm font-medium text-slate-700 transition duration-200 hover:border-white/45 hover:bg-white/75 dark:border-white/10 dark:bg-white/[0.05] dark:text-slate-200 dark:hover:bg-white/10"
                            >
                              {t(link.labelKey)}
                            </Link>
                          ))}
                        </div>

                        <div className="flex flex-col gap-2.5 border-t border-white/35 pt-4 dark:border-white/10">
                          <Button asChild className={`${primaryCtaClass} w-full`}>
                            <Link href={appHref} onClick={() => setMobileMenuOpen(false)}>
                              {appLabel}
                              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-white transition group-hover:scale-105">
                                <ArrowUpRight aria-hidden className="h-3.5 w-3.5" strokeWidth={2.2} />
                              </span>
                            </Link>
                          </Button>
                        </div>

                        <div className="flex items-center gap-2 rounded-2xl border border-white/30 bg-white/45 p-2.5 backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.05]">
                          <ModeToggle />
                          <LanguageDropdown />
                        </div>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
